/**
 * Dr. Salus — Monitor de Segurança
 * Painel da base curada de segurança médica + ocorrências catalogadas.
 *
 * Aba "Ocorrências": cada resposta do Dr. Salus é auditada em background por
 * um validador leve (gpt-5.4-mini) contra a base de regras; o que for marcado
 * (violação ou oportunidade perdida) aparece aqui para revisão humana.
 * Catalogação apenas — nada é bloqueado nem alterado na conversa.
 *
 * Aba "Regras": CRUD da base curada (intoxicações, interações medicamentosas,
 * populações de risco, sintomas graves, escopo clínico).
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  ShieldAlert, RefreshCw, Loader2, AlertCircle, Check, X,
  ChevronDown, ChevronRight, Plus, Pencil, Trash2, AlertTriangle,
  CheckCircle2, Eye, EyeOff, Search,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import safetyService from '../../services/dr-salus/safetyService';

const CATEGORY_LABELS = {
  intoxicacao_dose: 'Intoxicação / dose',
  interacao_medicamentosa: 'Interação medicamentosa',
  interacao_alimento_medicamento: 'Alimento × medicamento',
  populacao_de_risco: 'População de risco',
  sintoma_grave: 'Sintoma grave',
  escopo_clinico: 'Escopo clínico',
};

const SEVERITY_META = {
  critico: { label: 'Crítico', color: '#ef4444' },
  atencao: { label: 'Atenção', color: '#f59e0b' },
  info: { label: 'Info', color: '#3b82f6' },
};

const TIPO_LABELS = {
  violacao: 'Violação',
  oportunidade_perdida: 'Oportunidade perdida',
  ok: 'OK',
  erro_validador: 'Erro do validador',
};

const EMPTY_RULE_FORM = {
  title: '', rule: '', category: 'intoxicacao_dose', severity: 'atencao',
  triggersText: '', source: '',
};

function formatTs(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

export default function SafetyMonitorPage() {
  const { colors } = useTheme();
  const [tab, setTab] = useState('events');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [rules, setRules] = useState([]);
  const [expandedEvent, setExpandedEvent] = useState(null);

  // Filtros — ocorrências
  const [onlyFlagged, setOnlyFlagged] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterPhone, setFilterPhone] = useState('');

  // Form de regra (criar/editar)
  const [ruleForm, setRuleForm] = useState(null); // {id?} + EMPTY_RULE_FORM
  const [savingRule, setSavingRule] = useState(false);
  const [showInactiveRules, setShowInactiveRules] = useState(false);

  const flashSuccess = (m) => {
    setSuccessMsg(m);
    setTimeout(() => setSuccessMsg(null), 3000);
  };

  const load = useCallback(async (opts = {}) => {
    if (opts.initial) setLoading(true);
    else setRefreshing(true);
    setError(null);
    try {
      const [statsRes, eventsRes, rulesRes] = await Promise.all([
        safetyService.getStats().catch(() => null),
        safetyService.listEvents({
          flagged: onlyFlagged ? true : undefined,
          status: filterStatus || undefined,
          category: filterCategory || undefined,
          phone: filterPhone || undefined,
          limit: 200,
        }),
        safetyService.listRules(),
      ]);
      if (statsRes?.stats) setStats(statsRes.stats);
      setEvents(eventsRes.events || []);
      setRules(rulesRes.rules || []);
    } catch {
      setError('Erro ao carregar o Monitor de Segurança.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [onlyFlagged, filterStatus, filterCategory, filterPhone]);

  useEffect(() => { load({ initial: true }); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => { if (!loading) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [onlyFlagged, filterStatus, filterCategory]);

  const handleEventStatus = async (ev, status) => {
    try {
      await safetyService.updateEvent(ev.id, { status });
      setEvents(prev => prev.map(e => (e.id === ev.id ? { ...e, status } : e)));
      flashSuccess(status === 'revisado' ? 'Ocorrência marcada como revisada.' : 'Ocorrência ignorada.');
    } catch {
      setError('Erro ao atualizar ocorrência.');
    }
  };

  const openCreateRule = () => setRuleForm({ ...EMPTY_RULE_FORM });
  const openEditRule = (r) => setRuleForm({
    id: r.id,
    title: r.title || '',
    rule: r.rule || '',
    category: r.category || 'intoxicacao_dose',
    severity: r.severity || 'atencao',
    triggersText: (r.triggers || []).join(', '),
    source: r.source || '',
  });

  const saveRule = async () => {
    if (!ruleForm.title.trim() || ruleForm.rule.trim().length < 10) {
      setError('Preencha título e a regra (mínimo 10 caracteres).');
      return;
    }
    setSavingRule(true);
    setError(null);
    try {
      const payload = {
        title: ruleForm.title.trim(),
        rule: ruleForm.rule.trim(),
        category: ruleForm.category,
        severity: ruleForm.severity,
        triggers: ruleForm.triggersText.split(',').map(t => t.trim()).filter(Boolean),
        source: ruleForm.source.trim(),
      };
      if (ruleForm.id) {
        const res = await safetyService.updateRule(ruleForm.id, payload);
        setRules(prev => prev.map(r => (r.id === ruleForm.id ? res.rule : r)));
        flashSuccess('Regra atualizada.');
      } else {
        const res = await safetyService.createRule(payload);
        setRules(prev => [res.rule, ...prev]);
        flashSuccess('Regra criada — o validador passa a usá-la em até 5 min.');
      }
      setRuleForm(null);
    } catch {
      setError('Erro ao salvar regra.');
    } finally {
      setSavingRule(false);
    }
  };

  const toggleRuleActive = async (r) => {
    try {
      if (r.active) {
        await safetyService.deactivateRule(r.id);
        setRules(prev => prev.map(x => (x.id === r.id ? { ...x, active: false } : x)));
        flashSuccess('Regra desativada.');
      } else {
        const res = await safetyService.updateRule(r.id, { active: true });
        setRules(prev => prev.map(x => (x.id === r.id ? res.rule : x)));
        flashSuccess('Regra reativada.');
      }
    } catch {
      setError('Erro ao alterar regra.');
    }
  };

  const styles = {
    container: { padding: 24, height: '100%', overflowY: 'auto', backgroundColor: colors.bgPrimary },
    header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
    title: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 20, fontWeight: 700, color: colors.textPrimary },
    subtitle: { fontSize: 13, color: colors.textSecondary, marginTop: 4, maxWidth: 720 },
    statsRow: { display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' },
    statCard: {
      backgroundColor: colors.bgSecondary, border: `1px solid ${colors.borderColor}`,
      borderRadius: 10, padding: '12px 18px', minWidth: 140,
    },
    statValue: { fontSize: 22, fontWeight: 700, color: colors.textPrimary },
    statLabel: { fontSize: 12, color: colors.textSecondary, marginTop: 2 },
    tabs: { display: 'flex', gap: 4, marginBottom: 16, borderBottom: `1px solid ${colors.borderColor}` },
    tabBtn: (active) => ({
      padding: '10px 16px', border: 'none', background: 'transparent', cursor: 'pointer',
      fontSize: 14, fontWeight: active ? 700 : 500,
      color: active ? colors.textPrimary : colors.textSecondary,
      borderBottom: active ? '2px solid #3b82f6' : '2px solid transparent',
    }),
    toolbar: { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' },
    input: {
      padding: '8px 10px', borderRadius: 8, border: `1px solid ${colors.borderColor}`,
      backgroundColor: colors.bgSecondary, color: colors.textPrimary, fontSize: 13,
    },
    select: {
      padding: '8px 10px', borderRadius: 8, border: `1px solid ${colors.borderColor}`,
      backgroundColor: colors.bgSecondary, color: colors.textPrimary, fontSize: 13,
    },
    btn: {
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8,
      border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgSecondary,
      color: colors.textPrimary, fontSize: 13, cursor: 'pointer',
    },
    btnPrimary: {
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8,
      border: 'none', backgroundColor: '#3b82f6', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
    },
    card: {
      backgroundColor: colors.bgSecondary, border: `1px solid ${colors.borderColor}`,
      borderRadius: 10, marginBottom: 10, overflow: 'hidden',
    },
    cardHead: { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', cursor: 'pointer' },
    badge: (color) => ({
      display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700,
      color: '#fff', backgroundColor: color,
    }),
    badgeSoft: {
      display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600,
      color: colors.textSecondary, border: `1px solid ${colors.borderColor}`,
    },
    monoBlock: {
      whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.5, color: colors.textPrimary,
      backgroundColor: colors.bgPrimary, border: `1px solid ${colors.borderColor}`,
      borderRadius: 8, padding: 12,
    },
    fieldLabel: { fontSize: 12, fontWeight: 700, color: colors.textSecondary, margin: '10px 0 4px' },
    alert: (bg, fg) => ({
      display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 8,
      backgroundColor: bg, color: fg, fontSize: 13, marginBottom: 12,
    }),
    modalOverlay: {
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    },
    modal: {
      backgroundColor: colors.bgSecondary, borderRadius: 12, padding: 20, width: 560,
      maxWidth: '92vw', maxHeight: '86vh', overflowY: 'auto', border: `1px solid ${colors.borderColor}`,
    },
  };

  if (loading) {
    return (
      <div style={{ ...styles.container, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader2 size={28} style={{ animation: 'spin 1s linear infinite', color: colors.textSecondary }} />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <div style={styles.title}><ShieldAlert size={22} color="#ef4444" /> Monitor de Segurança</div>
          <div style={styles.subtitle}>
            Cada resposta do Dr. Salus é auditada em background contra a base curada de segurança
            médica (intoxicações, interações, populações de risco). Nada é bloqueado — as ocorrências
            ficam aqui para revisão e para evoluir a instrução do agente.
          </div>
        </div>
        <button style={styles.btn} onClick={() => load()} disabled={refreshing}>
          <RefreshCw size={14} style={refreshing ? { animation: 'spin 1s linear infinite' } : {}} /> Atualizar
        </button>
      </div>

      {error && <div style={styles.alert('#fee2e2', '#991b1b')}><AlertCircle size={16} /> {error}</div>}
      {successMsg && <div style={styles.alert('#dcfce7', '#166534')}><CheckCircle2 size={16} /> {successMsg}</div>}

      {stats && (
        <div style={styles.statsRow}>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{stats.total_avaliadas}</div>
            <div style={styles.statLabel}>respostas avaliadas</div>
          </div>
          <div style={styles.statCard}>
            <div style={{ ...styles.statValue, color: stats.flagged > 0 ? '#ef4444' : colors.textPrimary }}>{stats.flagged}</div>
            <div style={styles.statLabel}>ocorrências marcadas</div>
          </div>
          <div style={styles.statCard}>
            <div style={{ ...styles.statValue, color: stats.novas_pendentes > 0 ? '#f59e0b' : colors.textPrimary }}>{stats.novas_pendentes}</div>
            <div style={styles.statLabel}>pendentes de revisão</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{rules.filter(r => r.active).length}</div>
            <div style={styles.statLabel}>regras ativas na base</div>
          </div>
        </div>
      )}

      <div style={styles.tabs}>
        <button style={styles.tabBtn(tab === 'events')} onClick={() => setTab('events')}>Ocorrências</button>
        <button style={styles.tabBtn(tab === 'rules')} onClick={() => setTab('rules')}>Base curada (regras)</button>
      </div>

      {tab === 'events' && (
        <>
          <div style={styles.toolbar}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: colors.textPrimary, cursor: 'pointer' }}>
              <input type="checkbox" checked={onlyFlagged} onChange={e => setOnlyFlagged(e.target.checked)} />
              Só marcadas
            </label>
            <select style={styles.select} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="">Todos os status</option>
              <option value="novo">Novo</option>
              <option value="revisado">Revisado</option>
              <option value="ignorado">Ignorado</option>
            </select>
            <select style={styles.select} value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
              <option value="">Todas as categorias</option>
              {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <form onSubmit={(e) => { e.preventDefault(); load(); }} style={{ display: 'flex', gap: 6 }}>
              <input
                style={styles.input}
                placeholder="Buscar por telefone…"
                value={filterPhone}
                onChange={e => setFilterPhone(e.target.value)}
              />
              <button type="submit" style={styles.btn}><Search size={14} /></button>
            </form>
          </div>

          {events.length === 0 && (
            <div style={{ color: colors.textSecondary, fontSize: 14, padding: 24, textAlign: 'center' }}>
              Nenhuma ocorrência com esses filtros. 🎉
            </div>
          )}

          {events.map(ev => {
            const sev = SEVERITY_META[ev.severity] || null;
            const expanded = expandedEvent === ev.id;
            return (
              <div key={ev.id} style={styles.card}>
                <div style={styles.cardHead} onClick={() => setExpandedEvent(expanded ? null : ev.id)}>
                  {expanded ? <ChevronDown size={16} color={colors.textSecondary} /> : <ChevronRight size={16} color={colors.textSecondary} />}
                  {ev.flagged
                    ? <AlertTriangle size={16} color={sev?.color || '#f59e0b'} />
                    : <CheckCircle2 size={16} color="#22c55e" />}
                  <span style={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary }}>
                    {TIPO_LABELS[ev.tipo] || ev.tipo}
                  </span>
                  {sev && <span style={styles.badge(sev.color)}>{sev.label}</span>}
                  {(ev.categories || []).map(c => (
                    <span key={c} style={styles.badgeSoft}>{CATEGORY_LABELS[c] || c}</span>
                  ))}
                  <span style={{ marginLeft: 'auto', fontSize: 12, color: colors.textSecondary }}>
                    {ev.phone} · {formatTs(ev.ts)}
                  </span>
                  <span style={styles.badgeSoft}>{ev.status}</span>
                </div>
                {expanded && (
                  <div style={{ padding: '0 14px 14px' }}>
                    {ev.motivo && (
                      <>
                        <div style={styles.fieldLabel}>MOTIVO</div>
                        <div style={{ fontSize: 13, color: colors.textPrimary }}>{ev.motivo}</div>
                      </>
                    )}
                    {ev.comportamento_esperado && (
                      <>
                        <div style={styles.fieldLabel}>COMPORTAMENTO ESPERADO</div>
                        <div style={{ fontSize: 13, color: colors.textPrimary }}>{ev.comportamento_esperado}</div>
                      </>
                    )}
                    {ev.trecho && (
                      <>
                        <div style={styles.fieldLabel}>TRECHO PROBLEMÁTICO</div>
                        <div style={{ ...styles.monoBlock, borderColor: '#ef4444' }}>{ev.trecho}</div>
                      </>
                    )}
                    <div style={styles.fieldLabel}>MENSAGEM DO USUÁRIO</div>
                    <div style={styles.monoBlock}>{ev.user_message || '—'}</div>
                    <div style={styles.fieldLabel}>RESPOSTA DO DR. SALUS</div>
                    <div style={styles.monoBlock}>{ev.agent_response || '—'}</div>
                    {(ev.rule_ids || []).length > 0 && (
                      <>
                        <div style={styles.fieldLabel}>REGRAS APLICADAS</div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {ev.rule_ids.map(id => <span key={id} style={styles.badgeSoft}>{id}</span>)}
                        </div>
                      </>
                    )}
                    <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                      {ev.status !== 'revisado' && (
                        <button style={styles.btnPrimary} onClick={() => handleEventStatus(ev, 'revisado')}>
                          <Check size={14} /> Marcar revisado
                        </button>
                      )}
                      {ev.status !== 'ignorado' && (
                        <button style={styles.btn} onClick={() => handleEventStatus(ev, 'ignorado')}>
                          <X size={14} /> Ignorar
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}

      {tab === 'rules' && (
        <>
          <div style={styles.toolbar}>
            <button style={styles.btnPrimary} onClick={openCreateRule}><Plus size={14} /> Nova regra</button>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: colors.textPrimary, cursor: 'pointer' }}>
              <input type="checkbox" checked={showInactiveRules} onChange={e => setShowInactiveRules(e.target.checked)} />
              Mostrar desativadas
            </label>
          </div>

          {rules
            .filter(r => showInactiveRules || r.active)
            .map(r => {
              const sev = SEVERITY_META[r.severity] || SEVERITY_META.atencao;
              return (
                <div key={r.id} style={{ ...styles.card, opacity: r.active ? 1 : 0.55 }}>
                  <div style={{ ...styles.cardHead, cursor: 'default' }}>
                    <span style={styles.badge(sev.color)}>{sev.label}</span>
                    <span style={styles.badgeSoft}>{CATEGORY_LABELS[r.category] || r.category}</span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary }}>{r.title}</span>
                    <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                      <button style={styles.btn} title="Editar" onClick={() => openEditRule(r)}><Pencil size={14} /></button>
                      <button
                        style={styles.btn}
                        title={r.active ? 'Desativar' : 'Reativar'}
                        onClick={() => toggleRuleActive(r)}
                      >
                        {r.active ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </span>
                  </div>
                  <div style={{ padding: '0 14px 14px' }}>
                    <div style={{ fontSize: 13, lineHeight: 1.55, color: colors.textPrimary }}>{r.rule}</div>
                    {(r.triggers || []).length > 0 && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                        {r.triggers.map(t => <span key={t} style={styles.badgeSoft}>{t}</span>)}
                      </div>
                    )}
                    {r.source && (
                      <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 8 }}>Fonte: {r.source}</div>
                    )}
                  </div>
                </div>
              );
            })}
        </>
      )}

      {ruleForm && (
        <div style={styles.modalOverlay} onClick={() => !savingRule && setRuleForm(null)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 700, color: colors.textPrimary, marginBottom: 12 }}>
              {ruleForm.id ? 'Editar regra' : 'Nova regra da base curada'}
            </div>
            <div style={styles.fieldLabel}>TÍTULO</div>
            <input
              style={{ ...styles.input, width: '100%', boxSizing: 'border-box' }}
              value={ruleForm.title}
              onChange={e => setRuleForm(f => ({ ...f, title: e.target.value }))}
              placeholder="Ex.: Castanha-do-pará — excesso de selênio"
            />
            <div style={styles.fieldLabel}>REGRA (o que o agente deve reconhecer/fazer)</div>
            <textarea
              style={{ ...styles.input, width: '100%', minHeight: 110, boxSizing: 'border-box', resize: 'vertical' }}
              value={ruleForm.rule}
              onChange={e => setRuleForm(f => ({ ...f, rule: e.target.value }))}
              placeholder="Descreva o risco, o limite e o comportamento esperado do agente…"
            />
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={styles.fieldLabel}>CATEGORIA</div>
                <select
                  style={{ ...styles.select, width: '100%' }}
                  value={ruleForm.category}
                  onChange={e => setRuleForm(f => ({ ...f, category: e.target.value }))}
                >
                  {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <div style={styles.fieldLabel}>SEVERIDADE</div>
                <select
                  style={{ ...styles.select, width: '100%' }}
                  value={ruleForm.severity}
                  onChange={e => setRuleForm(f => ({ ...f, severity: e.target.value }))}
                >
                  <option value="critico">Crítico</option>
                  <option value="atencao">Atenção</option>
                  <option value="info">Info</option>
                </select>
              </div>
            </div>
            <div style={styles.fieldLabel}>PALAVRAS-CHAVE (separadas por vírgula)</div>
            <input
              style={{ ...styles.input, width: '100%', boxSizing: 'border-box' }}
              value={ruleForm.triggersText}
              onChange={e => setRuleForm(f => ({ ...f, triggersText: e.target.value }))}
              placeholder="castanha-do-pará, selênio, castanhas"
            />
            <div style={styles.fieldLabel}>FONTE / ORIGEM</div>
            <input
              style={{ ...styles.input, width: '100%', boxSizing: 'border-box' }}
              value={ruleForm.source}
              onChange={e => setRuleForm(f => ({ ...f, source: e.target.value }))}
              placeholder="Ex.: Relato do cliente em 19/08/2026"
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button style={styles.btn} disabled={savingRule} onClick={() => setRuleForm(null)}>Cancelar</button>
              <button style={styles.btnPrimary} disabled={savingRule} onClick={saveRule}>
                {savingRule ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Check size={14} />}
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
